/**
 * useNetworkInfo
 * Lightweight wrapper around the Network Information API to expose:
 * - effectiveType (slow-2g | 2g | 3g | 4g)
 * - saveData flag
 * - downlink/rtt (best-effort)
 * - devicePixelRatio
 *
 * DPR (Device Pixel Ratio) in plain English:
 * - It’s how many physical screen pixels fit into one CSS pixel.
 * - Retina/hi‑dpi screens often have DPR ~2, meaning they can display sharper images.
 * - We can request images up to DPR× wider to keep them crisp.
 * - On slow connections or when “Save Data” is on, we cap DPR to reduce download size.
 *
 * Used by media components to adapt image quality, DPR scaling, and video preloading/autoplay.
 * Falls back safely when the API is unavailable (SSR or unsupported browsers).
 */
import { useEffect, useState } from 'react';

interface NetworkInfo {
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | undefined;
  saveData: boolean;
  downlink: number;
  rtt: number;
  devicePixelRatio: number;
}

// Get the browser's network information object if supported,
// otherwise return null so the hook can fall back safely.
const getConnectionSpeed = (): NetworkInformation | null => {
  if (typeof window === 'undefined') return null;
  return (
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection ||
    null
  );
};

export const useNetworkInfo = (): NetworkInfo => {
  // Start with sensible defaults so SSR/unsupported browsers don't break.
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>({
    effectiveType: undefined,
    saveData: false,
    downlink: 10,
    rtt: 50,
    // Use a stable default of 1 so SSR markup matches the first client render.
    // We update to the real DPR in the effect below to avoid hydration mismatches.
    devicePixelRatio: 1
  });

  useEffect(() => {
    // 1) Skip on server (no window).
    if (typeof window === 'undefined') return;

    // 2) Ask the browser for its connection info (if supported).
    const connection = getConnectionSpeed();
    if (!connection) {
      return;
    }

    // 3) Define a small helper to read the latest values
    //    and stash them in React state.
    const updateNetworkInfo = () => {
      const conn = getConnectionSpeed();
      if (!conn) return;

      setNetworkInfo({
        effectiveType: (conn as any)
          .effectiveType as NetworkInfo['effectiveType'],
        saveData: (conn as any).saveData === true,
        downlink: (conn as any).downlink || 10,
        rtt: (conn as any).rtt || 50,
        devicePixelRatio: window.devicePixelRatio || 1
      });
    };

    // 4) Initialize immediately so components can react on first render.
    updateNetworkInfo();

    // 5) Subscribe to connection changes (if supported)
    //    so we adapt when the user moves between networks.
    if ((connection as any).addEventListener) {
      (connection as any).addEventListener('change', updateNetworkInfo);
      return () => {
        // 6) Clean up the listener on unmount.
        (connection as any).removeEventListener('change', updateNetworkInfo);
      };
    }
  }, []);

  return networkInfo;
};
